import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { ColumnConfig, FormColumnConfig, types } from "../../../../../interfaces/entity";
import RenderFormItem from "../../../../../components/Forms/FormItems";
import { Button, Checkbox, Col, Form, Input, Row, Select, Space, Tabs } from "antd";
import DynamicFormList from "./childrenForm";
import { DeleteOutlined } from "@ant-design/icons";
import { Editor } from "@monaco-editor/react";
import * as Babel from '@babel/standalone';
import { getFuncParseValue } from "../../../../../constants/functions";
const { TextArea } = Input;

interface fieldProps {
    id: string;
    label: string;
    type: types;
}

const availableFields: fieldProps[] = [
    { id: "text-input", label: "Text Input", type: "text" },
    { id: "text-number", label: "Number Input", type: "number" },
    { id: "text-area", label: "Text Area", type: "textarea" },
    { id: "select", label: "Select Dropdown", type: "select" },
    { id: "dynamic-select", label: "Dynamic Select", type: "DynamicSelect" },
    { id: "date-picker", label: "Date Picker", type: "date" },
    { id: "time-picker", label: "Time Picker", type: "time" },
    { id: "checkbox", label: "Checkbox", type: "checkbox" },
    { id: "checkbox-group", label: "Checkbox Group", type: "checkboxGroup" },
    { id: "radio", label: "Radio Group", type: "radio" },
    { id: "divider", label: "Divider", type: "divider" },
    { id: "rate", label: "Rate", type: "rate" },
    { id: "quill", label: "Rich Text Editor", type: "quill" },
    { id: "custom", label: "Custom", type: "custom" },
    { id: "file", label: "File Upload", type: "file" },
];

interface FormBuilderProps {
    onFormValueChange: (value: any) => void;
    onComboSet: (value: any) => void;
    formValue: any;
}

const FormBuilder: React.FC<FormBuilderProps> = ({ onFormValueChange, onComboSet, formValue }) => {
    const [formFields, setFormFields] = useState<FormColumnConfig[]>([]);
    const [selectedField, setSelectedField] = useState<FormColumnConfig | null>(null);
    const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
    const [selectedPattern, setSelectedPattern] = useState("");
    const [combo, setCombo] = useState<any>([]);
    const array: any = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    useEffect(() => {
        if (formValue) {
            setFormFields(formValue);
        }
    }, [formValue]);

    useEffect(() => {
        onFormValueChange(formFields);
        onComboSet(combo);
    }, [formFields, combo]);

    const setConboValues = (fieldName, name, value) => {
        const _combo = combo.map((field) => {
            if (field.name === fieldName) {
                return { ...field, [name]: value };
            }
            return field;
        });
        setCombo(_combo);
    }

    const onSave = () => {
        setFormFields(prev => prev.map((field) => {
            if (field.id === selectedField?.id) {
                return selectedField;
            }
            return field;
        }));
    }

    useEffect(() => {
        onSave();
    }, [selectedField]);


    const handleDragStart = (start: any) => {
        setDraggingItemId(start.draggableId);
    };

    const onRemove = (index: number) => {
        selectedField?.id === formFields[index].id && setSelectedField(null);
        setFormFields((prev) => prev.filter((_, i) => i !== index));
    }


    const handleDragEnd = (result: DropResult) => {
        setDraggingItemId(null);
        const { source, destination } = result;
        if (!destination) return;

        if (source.droppableId === "sidebar" && destination.droppableId === "form") {
            const newField = availableFields.find((field) => field.id === result.draggableId);
            if (newField) {
                const newFieldWithUniqueId = { ...newField, id: `${newField.id}-${Date.now()}` };
                const updatedFields = [...formFields];
                updatedFields.splice(destination.index, 0, newFieldWithUniqueId);
                updatedFields.forEach((field, index) => {
                    field.order = index;
                });
                setFormFields(updatedFields);
            }
        } else if (source.droppableId === "form" && destination.droppableId === "form") {
            const updatedFields = [...formFields];
            const [movedField] = updatedFields.splice(source.index, 1);
            updatedFields.splice(destination.index, 0, movedField);
            updatedFields.forEach((field, index) => {
                field.order = index;
            });
            setFormFields(updatedFields);
        }
    };

    const getPropsValue = (_selectedField) => {
        const propMap = {
            text: 'inputProps',
            textarea: 'inputTextAreaProps',
            select: 'selectProps',
            checkbox: 'checkboxProps',
            radio: 'radioProps',
            file: 'uploadProps',
            date: 'dateProps',
        };

        const keyName = propMap[_selectedField?.type];
        return keyName ? { value: _selectedField?.[keyName], keyName } : {};
    };

    const propInfo = getPropsValue(selectedField);
    const defaultValue = propInfo.value ? JSON.stringify(propInfo.value, null, 2) : '{}';


    return (
        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
            <div className="grid grid-cols-[min-content,1fr,min-content] gap-4 p-4">
                <Droppable droppableId="sidebar" isDropDisabled={true}>
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="bg-white p-4 w-60 border rounded overflow-y-auto" style={{ height: "calc(100vh - 275px)" }}>
                            <h3 className="font-semibold mb-2">Fields</h3>
                            {availableFields.map((field, index) => (
                                <Draggable key={field.id} draggableId={field.id} index={index}>
                                    {(provided, snapshot) => (
                                        <>
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`border-b border-b-gray-200 p-2 mb-2 rounded cursor-pointer ${snapshot.isDragging ? "opacity-50" : ""
                                                    }`}
                                            >
                                                ⋮⋮ {field.label}
                                            </div>
                                            {draggingItemId === field.id && snapshot.isDragging && (
                                                <div className="border-b border-b-gray-200 p-2 mb-2 rounded opacity-50">
                                                    ⋮⋮ {field.label}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>

                {/* Form Canvas - Drop Fields Here */}
                <Droppable droppableId="form">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="bg-white p-4 min-h-[300px] border rounded overflow-y-auto" style={{ height: "calc(100vh - 275px)" }}>
                            <h3 className="font-semibold mb-2">Form Builder</h3>
                            {formFields.length === 0 ? (
                                <p className="text-gray-400 max-w-3xl mx-auto">Drag fields here</p>
                            ) : (
                                <Form layout="vertical" className="max-w-3xl mx-auto">
                                    <Row gutter={16}>
                                        {formFields.map((field, index) => (
                                            <Col span={field?.span ?? 8} key={index}>
                                                <Draggable key={field.id} draggableId={field.id} index={index}>
                                                    {(provided) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            className={`w-full p-2 mb-2 rounded flex justify-between group cursor-pointer ${selectedField?.id === field.id ? 'border border-blue-500' : 'border border-gray-100'}`}
                                                            onClick={() => setSelectedField(field)}
                                                        >
                                                            <RenderFormItem column={{
                                                                type: field.type,
                                                                label: field.label,
                                                                style: { width: "100%" },
                                                                ...field,
                                                                formItem: field.type == 'custom' ? getFuncParseValue(field.rawData) : field.formItem,
                                                            }}
                                                                combo={combo}
                                                            />
                                                            <Space direction="horizontal" className="group-hover:block hidden" >
                                                                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onRemove(index)} />
                                                            </Space>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            </Col>
                                        ))}
                                    </Row>
                                </Form>
                            )}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
                <div className="bg-white p-4 w-100 border rounded">
                    <h3 className="font-semibold mb-2">Field Settings</h3>
                    {selectedField ? <Tabs defaultActiveKey="1">
                        <Tabs.TabPane tab="Settings" key="1">
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Label</label>
                                    <Input
                                        type="text"
                                        value={selectedField.label as string}
                                        onChange={(e) =>
                                            setSelectedField({ ...selectedField, label: e.target.value })
                                        }
                                        className="w-full border rounded p-1 mt-1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Name</label>
                                    <Input
                                        type="text"
                                        value={selectedField.name}
                                        onChange={(e) =>
                                            setSelectedField({ ...selectedField, name: e.target.value })
                                        }
                                        className="w-full border rounded p-1 mt-1"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Span</label>
                                    <Select
                                        value={selectedField.span}
                                        onChange={(value) => setSelectedField({ ...selectedField, span: value })}
                                        className="w-full mt-1"
                                    >
                                        <Select.Option value={8}>8</Select.Option>
                                        <Select.Option value={12}>12</Select.Option>
                                        <Select.Option value={16}>16</Select.Option>
                                        <Select.Option value={24}>24</Select.Option>
                                        {[1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 13, 14, 15, 17, 18, 19, 20, 21, 22, 23].map((field, index) => (<Select.Option key={index} value={field}>{field}</Select.Option>))}
                                    </Select>
                                </div>
                                {selectedField?.type == 'custom' && <div>
                                    <label className="block text-sm font-medium text-gray-700">Custom Field</label>
                                    <Editor
                                        height="200px"
                                        width={'100%'}
                                        theme="vs-dark"
                                        language="javascript"
                                        defaultValue={`(e) => (<div>/*write your code*/</div>)`}
                                        options={{
                                            minimap: { enabled: false },
                                            fontSize: 14,
                                            automaticLayout: true,
                                            formatOnType: true,
                                        }}
                                        onChange={(value) => {
                                            try {
                                                setSelectedField({ ...selectedField, rawData: value });
                                            } catch (error) {
                                                console.error("Invalid function body:", error);
                                            }
                                        }}
                                    />
                                </div>}
                                {selectedField?.type == 'textarea' && <div>
                                    <label className="block text-sm font-medium text-gray-700">Rows</label>
                                    <Select
                                        value={selectedField.inputTextAreaProps?.rows}
                                        onChange={(value) => setSelectedField({ ...selectedField, inputTextAreaProps: { ...selectedField.inputTextAreaProps, rows: value } })}
                                        className="w-full mt-1"
                                    >
                                        {array.map((field, index) => (<Select.Option key={index} value={field}>{field}</Select.Option>))}
                                    </Select>
                                </div>}
                                {['select', 'checkboxGroup', 'checkbox', 'radio', 'DynamicSelect'].includes(selectedField.type) && <div className="pt-2">
                                    {['select', 'DynamicSelect'].includes(selectedField.type) && <div>
                                        <label className="block text-sm font-medium text-gray-700">Dyanamic Options</label>
                                        <Checkbox
                                            checked={selectedField.isCombo}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setCombo([...combo.filter(e => e.name !== selectedField?.name), { name: selectedField?.name, api: '', idKey: '', render: () => '' }]);
                                                } else {
                                                    setCombo(combo.filter(e => e.name !== selectedField?.name));
                                                }
                                                setSelectedField({ ...selectedField, isCombo: e.target.checked });
                                            }}
                                        />
                                    </div>}
                                    {selectedField.isCombo ? <div className="space-y-2">
                                        <Input type="text" placeholder="api" value={combo.find(e => e.name == selectedField?.name)?.api} onChange={(e) => setConboValues(selectedField?.name, 'api', e.target.value)} />
                                        <Input type="text" placeholder="idKey" value={combo.find(e => e.name == selectedField?.name)?.idKey} onChange={(e) => setConboValues(selectedField?.name, 'idKey', e.target.value)} />
                                        <Input type="text" placeholder="render" value={combo.find(e => e.name == selectedField?.name)?.render} onChange={(e) => setConboValues(selectedField?.name, 'render', e.target.value)} />
                                    </div> :
                                        <DynamicFormList options={selectedField?.children ?? []} updateOptions={(e) => setSelectedField({ ...selectedField, children: e })} />}
                                </div>}
                            </Space>
                        </Tabs.TabPane>
                        <Tabs.TabPane tab="Validations" key="2">
                            <Space direction="vertical" style={{ width: "100%" }}>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Required</label>
                                    <Checkbox
                                        checked={selectedField.rules?.some((rule: any) => rule.required)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedField({
                                                    ...selectedField,
                                                    rules: [
                                                        { required: true, message: 'This field is required' },
                                                    ],
                                                });
                                            } else {
                                                setSelectedField({ ...selectedField, rules: [] });
                                            }
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Minimum Length</label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={(selectedField.rules?.find((rule: any) => 'min' in rule) as any)?.min || ""}
                                        onChange={(e) => {
                                            const minLength = parseInt(e.target.value, 10);
                                            const updatedRules = selectedField.rules?.filter((rule: any) => !rule.min) || [];
                                            if (minLength > 0) {
                                                updatedRules.push({ min: minLength, message: `Minimum ${minLength} characters required` });
                                            }
                                            setSelectedField({ ...selectedField, rules: updatedRules });
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Maximum Length</label>
                                    <Input
                                        type="number"
                                        min={1}
                                        value={(selectedField.rules?.find((rule: any) => 'max' in rule) as any)?.max || ""}
                                        onChange={(e) => {
                                            const maxLength = parseInt(e.target.value, 10);
                                            const updatedRules = selectedField.rules?.filter((rule: any) => !rule.max) || [];
                                            if (maxLength > 0) {
                                                updatedRules.push({ max: maxLength, message: `Maximum ${maxLength} characters allowed` });
                                            }
                                            setSelectedField({ ...selectedField, rules: updatedRules });
                                        }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Pattern</label>
                                    <Select
                                        style={{ width: "100%" }}
                                        allowClear
                                        placeholder="Select a pattern"
                                        value={selectedField.rawData || ""}
                                        onChange={(value) => {
                                            setSelectedPattern(value);
                                            const pattern = value;
                                            const updatedRules = selectedField.rules?.filter((rule: any) => rule.required) || [];
                                            if (pattern === "email") {
                                                updatedRules.push({ type: "email", message: "Invalid email format" });
                                            } else if (pattern === "phone") {
                                                updatedRules.push({ pattern: /^[0-9]{10}$/, message: "Phone number must be 10 digits" });
                                            } else if (pattern === "url") {
                                                updatedRules.push({ type: "url", message: "Invalid URL format" });
                                            } else if (pattern === "alphanumeric") {
                                                updatedRules.push({ pattern: /^[a-zA-Z0-9]+$/, message: "Only alphanumeric characters allowed" });
                                            }
                                            setSelectedField({ ...selectedField, rules: updatedRules, rawData: pattern });
                                        }}
                                    >
                                        <Select.Option value="email">Email</Select.Option>
                                        <Select.Option value="phone">Phone (10 digits)</Select.Option>
                                        <Select.Option value="url">URL</Select.Option>
                                        <Select.Option value="alphanumeric">Alphanumeric</Select.Option>
                                        <Select.Option value="custom">Custom Pattern</Select.Option>
                                    </Select>
                                </div>
                                {selectedPattern === "custom" && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Custom Pattern (Regex)</label>
                                        <Input
                                            type="text"
                                            placeholder="Enter your regex pattern"
                                            value={(selectedField.rules?.find((rule: any) => 'pattern' in rule) as any)?.pattern || ""}
                                            onChange={(e) => {
                                                const customPattern = e.target.value;
                                                const updatedRules = selectedField.rules?.filter((rule: any) => !rule.pattern) || [];

                                                if (customPattern) {
                                                    try {
                                                        new RegExp(customPattern); // Validate regex format
                                                        updatedRules.push({ pattern: new RegExp(customPattern), message: "Invalid format according to the custom pattern" });
                                                    } catch (error) {
                                                        console.error("Invalid regex pattern");
                                                    }
                                                }

                                                setSelectedField({ ...selectedField, rules: updatedRules });
                                            }}
                                        />
                                    </div>
                                )}
                                {propInfo.keyName && <div>
                                    <label className="block text-sm font-medium text-gray-700">Props</label>
                                    <Editor
                                        height="200px"
                                        width="100%"
                                        theme="vs-dark"
                                        language="json"
                                        value={defaultValue}
                                        onChange={(value) => {
                                            try {
                                                const parsedValue = JSON.parse(value || "{}");
                                                if (propInfo.keyName) {
                                                    setSelectedField({
                                                        ...selectedField,
                                                        [propInfo.keyName]: parsedValue,
                                                    });
                                                }
                                            } catch (error) {
                                                console.error("Invalid JSON format", error);
                                            }
                                        }}
                                    />
                                </div>}
                            </Space>
                        </Tabs.TabPane>
                    </Tabs> : (
                        <p className="text-gray-400">Select a field to edit</p>
                    )}
                </div>
            </div>
        </DragDropContext>
    );
};

export default FormBuilder;
